import { Boundaries } from "@planzone/core";
import { Identifiable, Zone } from "@planzone/dto";
import { Event, events } from "@planzone/events";
import { createStateReducer } from "../../utils/createStateReducer";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { resetState } from "../../utils/resetState";
import { reduceResource } from "../../common/resource";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = [];

function handleRequest(state: State, zone: Identifiable<Zone>): State {
  return [
    ...state,
    { request: setRequestStateToPending(), zone, resources: [] },
  ];
}

function handleSuccess(
  state: State,
  { resources, zone }: Boundaries["retrieveZoneResources"]["success"],
): State {
  return state.map((res) =>
    res.zone.uuid === zone.uuid
      ? {
          ...res,
          request: setRequestStateToSuccess(),
          resources,
        }
      : res,
  );
}

function handleFailure(state: State, zone: Identifiable<Zone>): State {
  return state.map((res) =>
    res.zone.uuid === zone.uuid
      ? { ...res, request: setRequestStateToFailure() }
      : res,
  );
}

function handleMaterialResourceAddition(
  state: State,
  projectResource: Boundaries["addProjectMaterialResource"]["success"],
): State {
  return state.map((res) =>
    res.zone.uuid === projectResource.project.zone.uuid
      ? {
          ...res,
          request: setRequestStateToSuccess(),
          resources: [...res.resources, projectResource.resource],
        }
      : res,
  );
}

function handleVirtualResourceAddition(
  state: State,
  projectResource: Boundaries["addProjectVirtualResource"]["success"],
): State {
  return state.map((res) =>
    res.zone.uuid === projectResource.project.zone.uuid
      ? {
          ...res,
          request: setRequestStateToSuccess(),
          resources: [...res.resources, projectResource.resource],
        }
      : res,
  );
}

function handleResourceFromPlanzoneRemoval(
  state: State,
  { resource }: Boundaries["removeResourceFromPlanzone"]["success"],
): State {
  return state.map((res) => ({
    ...res,
    resources: res.resources.filter(({ uuid }) => resource.uuid !== uuid),
  }));
}

function propagateEvent(state = initialState, event: Event): State {
  return state.map((item) => ({
    ...item,
    resources: item.resources.map((resource) =>
      reduceResource(resource, event),
    ),
  }));
}

const reduceState = resetState(
  initialState,
  createStateReducer(
    initialState,
    {
      [events.retrieveZoneResourcesRequested]: handleRequest,
      [events.retrieveZoneResourcesSucceeded]: handleSuccess,
      [events.retrieveZoneResourcesFailed]: handleFailure,
      [events.addProjectMaterialResourceSucceeded]:
        handleMaterialResourceAddition,
      [events.addProjectVirtualResourceSucceeded]:
        handleVirtualResourceAddition,
      [events.removeResourceFromPlanzoneSucceeded]:
        handleResourceFromPlanzoneRemoval,
    },
    propagateEvent,
  ),
  [events.changePlanzoneSucceeded],
);

export type { State, Selector };

export { reduceState, initialState, createSelector };
